// TvCastSession — orchestrates the TV-side cast lifecycle:
//   1. Register with the signaling service, get deviceId + 6-digit code.
//   2. Heartbeat registration every 60s so the entry stays alive.
//   3. Poll the signaling mailbox; when an offer arrives, spin up TvPeer,
//      relay ICE candidates both ways until the data channel opens.
//   4. Surface phone → TV command messages to the consumer.
//
// All network calls are same-origin (the Pages Functions serve under /api/...).

import { TvPeer } from '@/webrtc/peer';
import type { PhoneToTvMessage, TvToPhoneMessage } from '@/webrtc/messages';

const HEARTBEAT_MS = 60_000;
const POLL_MS = 200;

// Persist the assigned deviceId so refreshes/reopens reuse the same
// identity. This is what makes the phone's "Recently paired" list work:
// if the TV came back with a fresh deviceId every refresh, recents would
// be permanently stale.
const STORAGE_KEY_DEVICE_ID = 'yc-tv-deviceId';
function readStoredDeviceId(): string | null {
  try { return typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY_DEVICE_ID) : null; }
  catch { return null; }
}
function writeStoredDeviceId(deviceId: string): void {
  try { if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY_DEVICE_ID, deviceId); }
  catch { /* private mode / quota — fine to lose */ }
}

interface RegisterResponse {
  deviceId: string;
  code: string;
  label: string;
  ttl: number;
}

interface SignalMessage {
  from: 'phone' | 'tv';
  type: 'offer' | 'answer' | 'ice' | 'bye';
  payload: unknown;
  ts: number;
}

export type SessionState =
  | 'initializing'
  | 'waiting'
  | 'negotiating'
  | 'connected'
  | 'reconnecting'
  | 'error';

export interface SessionSnapshot {
  state: SessionState;
  deviceId: string | null;
  code: string | null;
  label: string | null;
  errorMessage: string | null;
}

export interface TvCastSessionEvents {
  onSnapshot?: (snap: SessionSnapshot) => void;
  onCommand?: (msg: PhoneToTvMessage) => void;
}

export class TvCastSession {
  private events: TvCastSessionEvents;
  private snap: SessionSnapshot = {
    state: 'initializing',
    deviceId: null,
    code: null,
    label: null,
    errorMessage: null,
  };
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private abort = new AbortController();
  private since = 0;
  private peer: TvPeer | null = null;

  constructor(events: TvCastSessionEvents = {}) {
    this.events = events;
    const stored = readStoredDeviceId();
    if (stored) this.snap = { ...this.snap, deviceId: stored };
    // Ignore any signal messages with ts <= now — those are leftovers from
    // a previous session at this same deviceId (page refresh, browser
    // restart with persisted localStorage). The server-side SignalRoom
    // also clears its buffer on a new offer, but this is belt-and-suspenders.
    this.since = Date.now();
  }

  async start(): Promise<void> {
    await this.register();
    this.scheduleHeartbeat();
    void this.kickPoll();
  }

  /** Fire a poll immediately on start, then fall into the regular cadence. */
  private async kickPoll(): Promise<void> {
    if (this.abort.signal.aborted) return;
    await this.poll().catch(() => {});
    if (!this.abort.signal.aborted) this.schedulePoll();
  }

  stop(): void {
    this.abort.abort();
    if (this.heartbeatTimer) clearTimeout(this.heartbeatTimer);
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.peer?.close();
    this.peer = null;
    this.setState({ state: 'error', errorMessage: 'session stopped' });
  }

  getSnapshot(): SessionSnapshot {
    return { ...this.snap };
  }

  send(msg: TvToPhoneMessage): boolean {
    return this.peer?.send(msg) ?? false;
  }

  private setState(patch: Partial<SessionSnapshot>): void {
    this.snap = { ...this.snap, ...patch };
    this.events.onSnapshot?.(this.getSnapshot());
  }

  private async register(): Promise<void> {
    try {
      const res = await fetch('/api/register', {
        method: 'POST',
        signal: this.abort.signal,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          deviceId: this.snap.deviceId ?? undefined,
          userAgent: navigator.userAgent,
        }),
      });
      if (!res.ok) throw new Error(`register failed: ${res.status}`);
      const body = (await res.json()) as RegisterResponse;
      writeStoredDeviceId(body.deviceId);
      this.setState({
        deviceId: body.deviceId,
        code: body.code,
        label: body.label,
        state: this.snap.state === 'initializing' ? 'waiting' : this.snap.state,
      });
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      this.setState({
        state: 'error',
        errorMessage: `Could not reach cast server. ${(err as Error).message}`,
      });
      throw err;
    }
  }

  private scheduleHeartbeat(): void {
    this.heartbeatTimer = setTimeout(async () => {
      if (this.abort.signal.aborted) return;
      await this.register().catch(() => {});
      this.scheduleHeartbeat();
    }, HEARTBEAT_MS);
  }

  private schedulePoll(): void {
    this.pollTimer = setTimeout(async () => {
      if (this.abort.signal.aborted) return;
      await this.poll().catch(() => {});
      this.schedulePoll();
    }, POLL_MS);
  }

  private async poll(): Promise<void> {
    if (!this.snap.deviceId) return;
    // Once connected via WebRTC, signaling is no longer needed; slow the poll
    // cadence so we can pick up BYE without burning CPU.
    if (this.snap.state === 'connected') return;

    const url = `/api/signal/${encodeURIComponent(
      this.snap.deviceId,
    )}?since=${this.since}&consumer=tv`;
    const res = await fetch(url, { signal: this.abort.signal });
    if (!res.ok) return;
    const body = (await res.json()) as { messages: SignalMessage[]; until: number };
    this.since = body.until;
    for (const msg of body.messages) {
      await this.handleSignal(msg);
    }
  }

  private async handleSignal(msg: SignalMessage): Promise<void> {
    if (msg.type === 'offer') {
      await this.handleOffer(msg.payload as RTCSessionDescriptionInit);
    } else if (msg.type === 'ice' && this.peer) {
      await this.peer.addRemoteIceCandidate(msg.payload as RTCIceCandidateInit);
    } else if (msg.type === 'bye') {
      this.peer?.close();
      this.peer = null;
      this.setState({ state: 'waiting', errorMessage: null });
    }
  }

  private async handleOffer(offer: RTCSessionDescriptionInit): Promise<void> {
    // New offer arrived — clean up any stale peer and create a fresh one.
    this.peer?.close();
    this.setState({ state: 'negotiating' });

    this.peer = new TvPeer({
      onConnectionState: (state) => {
        if (state === 'connected') {
          this.setState({ state: 'connected' });
          this.peer?.send({ type: 'READY' });
        } else if (state === 'failed' || state === 'closed') {
          if (this.snap.state === 'connected') {
            this.setState({ state: 'reconnecting' });
          }
        }
      },
      onLocalIceCandidate: (candidate) => this.postSignal('ice', candidate),
      onLocalAnswer: (answer) => this.postSignal('answer', answer),
      onMessage: (m) => this.events.onCommand?.(m),
    });

    await this.peer.acceptOffer(offer);
  }

  private async postSignal(type: 'answer' | 'ice', payload: unknown): Promise<void> {
    if (!this.snap.deviceId) return;
    const url = `/api/signal/${encodeURIComponent(this.snap.deviceId)}`;
    await fetch(url, {
      method: 'POST',
      signal: this.abort.signal,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ from: 'tv', type, payload }),
    }).catch(() => {});
  }
}
